"use client";

import { Button, Card, Input, Statistic, Table, Typography } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import Link from "next/link";
import { useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { useCatalog } from "@/catalog/CatalogContext";
import { countByStatus, type Vendor } from "@/data/catalog";
import { VendorDrawer } from "./VendorDrawer";
import styles from "./catalog.module.css";

export function VendorsView() {
  const { vendors, segments } = useCatalog();
  const router = useRouter();
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return vendors;
    return vendors.filter((vendor) => vendor.name.toLowerCase().includes(needle));
  }, [query, vendors]);

  const totals = useMemo(() => {
    const counts = countByStatus(segments);
    return {
      vendors: vendors.length,
      segments: counts.total,
      targetable: counts.ACTIVE,
    };
  }, [segments, vendors]);

  return (
    <div className={styles.page}>
      <div className={styles.heading}>
        <div>
          <Typography.Title level={2} style={{ margin: 0 }}>
            Vendors
          </Typography.Title>
          <Typography.Paragraph type="secondary" style={{ margin: "8px 0 0", maxWidth: 640 }}>
            Register a vendor, then add the audience segments it sells.
          </Typography.Paragraph>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>
          New vendor
        </Button>
      </div>

      <div className={styles.stats}>
        <Card size="small">
          <Statistic title="Vendors" value={totals.vendors} />
        </Card>
        <Card size="small">
          <Statistic title="Segments" value={totals.segments} />
        </Card>
        <Card size="small">
          <Statistic title="Targetable" value={totals.targetable} />
        </Card>
      </div>

      <Card>
        <div className={styles.toolbar} style={{ marginBottom: 16 }}>
          <Input
            className={styles.search}
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search vendors"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label="Search vendors"
          />
        </div>
        <Table<Vendor>
          rowKey="id"
          dataSource={rows}
          pagination={{ pageSize: 8, hideOnSinglePage: true }}
          locale={{ emptyText: query ? "No vendors match that search." : "No vendors yet." }}
          onRow={(vendor) => ({
            onClick: () => router.push(`/vendors/${vendor.id}`),
            style: { cursor: "pointer" },
          })}
          columns={[
            {
              title: "Vendor",
              dataIndex: "name",
              render: (name: string, vendor) => (
                <Link href={`/vendors/${vendor.id}`} onClick={(event) => event.stopPropagation()}>
                  {name}
                </Link>
              ),
            },
            {
              title: "Segments",
              key: "segments",
              render: (_, vendor) => {
                const counts = countByStatus(
                  segments.filter((segment) => segment.vendorId === vendor.id),
                );
                const parts = [
                  counts.ACTIVE ? `${counts.ACTIVE} active` : null,
                  counts.PENDING ? `${counts.PENDING} pending` : null,
                  counts.INACTIVE ? `${counts.INACTIVE} inactive` : null,
                ].filter(Boolean);
                return (
                  <div>
                    <div>{counts.total}</div>
                    <Typography.Text type="secondary">
                      {parts.length > 0 ? parts.join(" · ") : "None yet"}
                    </Typography.Text>
                  </div>
                );
              },
            },
          ]}
        />
      </Card>

      <VendorDrawer open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}
